import ContainerNavigationView from "../../common/view/ContainerNavigationView";
import Term from "../Term";
import styles from "../css/SelectableTermListScreen.module.css";
import { useTranslation } from "react-i18next";

import { ReactComponent as CloseIcon } from "../../../img/iconImgAllClose.svg";
import { useEffect, useState } from "react";
import TermListGrid from "../custom_view/TermListGrid";
import { getLearningPronunciationPositionFromLocalStroage } from "../../settings/util/learningSettings";
import { QuestionType } from "../../settings/model/QuestionType";
import SmallButton from "../../common/SmallButton";
import GroupSelectScreen from "../../groups/GroupSelectScreen";
import Group from "../../groups/Group";

import {
  updateTermsGroup as updateTermsGroupFromAPI,
  updateTermsLevel as updateTermsLevelFromAPI,
  deleteTerms as deleteTermsFromAPI,
} from "../../term/termAPI";

import { useAppDispatch } from "../../../hook";
import { updateTerms } from "../termSlice";
import { TermLevel, getCurrentLevelExp } from "../custom_view/TermLevel";
import OnevocaNewDialog from "../../common/view/OnevocaNewDialog";
import { OnevocaNewDialogRadioButton } from "../../common/model/OnevocaNewDialogRadioButton";
import OnevocaDialog from "../../common/OnevocaDialog";
import { useTerms } from "../useTerms";
import ListEmptyText from "../../common/ListEmptyText";

interface SelectableTermListScreenProps {
  showing: boolean;
  termList?: Term[];
  onUpdateTermList: (updatedTermList: Term[]) => void;
  onDeleteTermList: (deletedTermIds: string[]) => void;
  onClose: () => void;
}

export default function SelectableTermListScreen(
  props: SelectableTermListScreenProps
) {
  const { t } = useTranslation();

  const termsDispatch = useAppDispatch();
  const [terms] = useTerms();

  const [rawTermList, setRawTermList] = useState<Term[]>([]);

  const [selectedTermCount, setSelectedTermCount] = useState<number>(0);
  const [hasSelectedTermList, setHasSelectedTermList] =
    useState<boolean>(false);
  const [rightButtonText, setRightButtonText] = useState<string>("");

  const [showingGroupSelect, setShowingGroupSelect] = useState<boolean>(false);
  const [showingLevelSelect, setShowingLevelSelect] = useState<boolean>(false);
  const [showingDeleteDialog, setShowingDeleteDialog] =
    useState<boolean>(false);

  useEffect(() => {
    cancelSelection();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.showing]);

  useEffect(() => {
    if (props.termList === undefined) return;
    const newRawTermList = [...props.termList];
    setRawTermList(newRawTermList);
  }, [props.termList]);

  useEffect(() => {
    const selectedTermCount = rawTermList.filter((t) => t.selected).length;
    setSelectedTermCount(selectedTermCount);
    const rightTextString =
      selectedTermCount === 0
        ? t("SelectAllTerms")
        : t("SelectCancelAllTerms", {
            termsCount: selectedTermCount,
          });
    setRightButtonText(rightTextString);
    setHasSelectedTermList(selectedTermCount !== 0);
  }, [rawTermList, t]);

  function cancelSelection() {
    const newRawTermList = rawTermList.map((t) => ({ ...t, selected: false }));
    setRawTermList(newRawTermList);
  }

  function handleOnClickContainer(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
  }

  function handleOnClickTopNavigationRightTextButton() {
    if (hasSelectedTermList) {
      setRawTermList(rawTermList.map((t) => ({ ...t, selected: false })));
    } else {
      setRawTermList(rawTermList.map((t) => ({ ...t, selected: true })));
    }
  }

  function handleOnClickTermListEditGroupButton() {
    if (!hasSelectedTermList) return;
    setShowingGroupSelect(true);
  }

  function handleOnClickTermListEditLevelButton(
    e: React.MouseEvent<HTMLDivElement>
  ) {
    if (!hasSelectedTermList) return;
    setShowingLevelSelect(true);
  }

  function handleOnClickTermListDeleteButton() {
    if (!hasSelectedTermList) return;
    setShowingDeleteDialog(true);
  }

  function onTapTermCard(term: Term, isSelectionMode: boolean) {
    const index = rawTermList.findIndex((t) => t.id === term.id);
    rawTermList[index].selected = !rawTermList[index].selected;
    const newRawTermList = [...rawTermList];
    setRawTermList(newRawTermList);
  }

  async function updateTermsGroup(toTerms: Term[], toGroup: Group) {
    try {
      await updateTermsGroupFromAPI(toTerms, toGroup);
      updateTermsGroupToList(toTerms, toGroup);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
    }
  }

  async function updateTermLevel(toTerms: Term[], newLevel: TermLevel) {
    try {
      await updateTermsLevelFromAPI(toTerms, newLevel);
      updateTermsLevelToList(toTerms, newLevel);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
    }
  }

  function updateTermsGroupToList(toTerms: Term[], toGroup: Group) {
    const newGroup = toGroup.isUnspecifiedGroup ? "" : toGroup.title;
    const newTerms = rawTermList.map((term) => {
      const matchingTerm = toTerms.find((toTerm) => toTerm.id === term.id);
      if (matchingTerm) {
        return {
          ...term,
          group: newGroup,
        };
      }
      return term;
    });

    const serializedTerms = newTerms.map((term) => Term.toJson(term));
    termsDispatch(updateTerms(serializedTerms));
    setRawTermList(newTerms);
    props.onUpdateTermList(newTerms);
  }

  function updateTermsLevelToList(toTerms: Term[], toLevel: TermLevel) {
    const newTerms = rawTermList.map((term) => {
      const matchingTerm = toTerms.find((toTerm) => toTerm.id === term.id);
      if (matchingTerm) {
        return {
          ...term,
          level: toLevel,
          exp: getCurrentLevelExp(toLevel),
        };
      }
      return term;
    });

    const serializedTerms = newTerms.map((term) => Term.toJson(term));
    termsDispatch(updateTerms(serializedTerms));
    setRawTermList(newTerms);
    props.onUpdateTermList(newTerms);
  }

  async function deleteTermList(termList: Term[]) {
    try {
      await deleteTermsFromAPI(termList);
      deleteTermsToList(termList);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
    }
  }

  function deleteTermsToList(deleteTerms: Term[]) {
    if (props.termList === undefined) return;
    const copiedIds = deleteTerms.map((t: Term) => t.id);
    const idsToDelete = new Set(deleteTerms.map((term) => term.id));
    const newTerms = terms.filter((term) => !idsToDelete.has(term.id));
    const serializedTerms = newTerms.map((term) => Term.toJson(term));
    termsDispatch(updateTerms(serializedTerms));
    setRawTermList(newTerms);
    props.onDeleteTermList(copiedIds);
  }

  function handleOnClickLevelDoneButton(id: number) {
    const selectedTerms: Term[] = rawTermList.filter((term) => term.selected);

    if (id === 1) {
      updateTermLevel(selectedTerms, TermLevel.difficult);
    } else if (id === 2) {
      updateTermLevel(selectedTerms, TermLevel.familiar);
    } else if (id === 3) {
      updateTermLevel(selectedTerms, TermLevel.perfect);
    }

    setShowingLevelSelect(false);
  }

  if (!props.showing || props.termList === undefined) return null;

  return (
    <>
      <div className={styles.stack}>
        <div className={styles.main} onClick={props.onClose}>
          <div className={styles.container} onClick={handleOnClickContainer}>
            <div className={styles.contentContainer}>
              <ContainerNavigationView
                open={true}
                title={t("Edit")}
                leftImageButtonImage={CloseIcon}
                onClickLeftImageButton={props.onClose}
                rightTextButtonText={rightButtonText}
                rightTextButtonColor="var(--text-accent-color)"
                onClickRightTextButton={
                  handleOnClickTopNavigationRightTextButton
                }
              />

              <div className={styles.buttonContainer}>
                <SmallButton
                  title={t("EditGroupButton")}
                  variant={hasSelectedTermList ? "simple" : "disable"}
                  onClick={handleOnClickTermListEditGroupButton}
                />
                <SmallButton
                  title={t("EditLevelButton")}
                  variant={hasSelectedTermList ? "simple" : "disable"}
                  onClick={handleOnClickTermListEditLevelButton}
                />
                <SmallButton
                  title={t("Delete")}
                  variant={hasSelectedTermList ? "simple" : "disable"}
                  onClick={handleOnClickTermListDeleteButton}
                />
              </div>

              <div className={styles.listContainer}>
                <TermListGrid
                  terms={rawTermList}
                  handleOnClick={onTapTermCard}
                  handleOnClickMarkButton={() => {}}
                  handleOnClickLevelButton={() => {}}
                  handleOnClickMoreButton={() => {}}
                  handleOnClickGenerateSentenceButton={() => {}}
                  handleOnClickSentenceLanguageButton={() => {}}
                  handleOnClickMoreSentenceButton={() => {}}
                  handleOnClickSentenceMoreButton={() => {}}
                  config={{
                    style: "list",
                    questionType: QuestionType.showAll,
                    pronunciationPosition:
                      getLearningPronunciationPositionFromLocalStroage(),
                    isSelectionMode: true,
                    isShowDetailMode: true,
                    hideMoreButton: true,
                  }}
                />
              </div>
              {rawTermList.length === 0 && (
                <div className={styles.termsEmptyTextContainer}>
                  <ListEmptyText text={t("NoTermsText")} />
                </div>
              )}
            </div>
          </div>
        </div>

        <GroupSelectScreen
          open={showingGroupSelect}
          selectStyle="single"
          showAllGroup={false}
          showNoGroup={true}
          showGroupAddButton={true}
          handleOnSelectGroups={(groups: Group[]) => {
            const group = groups[0] ?? null;
            if (group === null || group === undefined) return;
            const selectedTerms: Term[] = rawTermList.filter(
              (term) => term.selected
            );
            updateTermsGroup(selectedTerms, group);
          }}
          handleOnClose={() => {
            setShowingGroupSelect(false);
          }}
        />

        <OnevocaNewDialog
          open={showingLevelSelect}
          title={t("EditLevelButton")}
          actionButtonTitle={t("Confirmation")}
          radioButtons={[
            new OnevocaNewDialogRadioButton(1, t("Difficult"), false),
            new OnevocaNewDialogRadioButton(2, t("Familiar"), false),
            new OnevocaNewDialogRadioButton(3, t("Perfect"), false),
          ]}
          canCancel={true}
          onDoneWithRadioButton={handleOnClickLevelDoneButton}
          onClose={() => {
            setShowingLevelSelect(false);
          }}
        />

        <OnevocaDialog
          open={showingDeleteDialog}
          title={t("Notice")}
          message={t("DeleteTermListMessage", { selectedTermCount })}
          buttons={[
            {
              title: t("Delete"),
              style: "solid",
              onClick: () => {
                if (!hasSelectedTermList) return;
                const selectedTerms: Term[] = rawTermList.filter(
                  (term) => term.selected
                );
                deleteTermList(selectedTerms);
                setShowingDeleteDialog(false);
              },
            },
            {
              title: t("Cancel"),
              style: "normal",
              onClick: () => {
                setShowingDeleteDialog(false);
              },
            },
          ]}
        />
      </div>
    </>
  );
}
